import { EditMode } from "PFApp/booking/types/edit_mode";
import { utc } from "PFCore/helpers/date";
import { BookingWithData } from "PFCore/hooks/queries/profile";
import { useCallback, useMemo } from "react";

import { useSelectModeState } from "../booking_edit_mode_store";
import { BulkAction, BulkDatesMode } from "../booking_edit_mode_store.types";
import {
  bulkDraftMove,
  bulkDraftUpdateDetails,
  bulkDraftUpdateDuration,
  bulkDraftUpdateEndDate,
  bulkDraftUpdateStartDate
} from "../utils/draft_bulk_operation.utils";
import { useDraftOperation } from "./use_draft_operation";

export type UseBulkDraftOperationReturn = {
  performBulkDraftOperation: (bookings: BookingWithData[]) => BookingWithData[];
};

export const useBulkDraftOperation = (): UseBulkDraftOperationReturn => {
  const { filteredBookings, bookingsRangeMinDate, valuesToUpdate } = useSelectModeState();
  const { bulkAction, dates, details, duration } = valuesToUpdate;
  const { mode, date } = dates;

  const affectedBookingIds = useMemo(() => filteredBookings.map(({ id }) => id), [filteredBookings]);

  const transformer = useCallback(
    (booking: BookingWithData) => {
      switch (bulkAction) {
        case BulkAction.BulkUpdateDates:
          if (mode === BulkDatesMode.BulkMove) {
            return bulkDraftMove(booking, date, bookingsRangeMinDate, utc);
          } else if (mode === BulkDatesMode.BulkUpdateStartDate) {
            return bulkDraftUpdateStartDate(booking, date, utc);
          } else if (mode === BulkDatesMode.BulkUpdateEndDate) {
            return bulkDraftUpdateEndDate(booking, date, utc);
          } else {
            return booking;
          }
        case BulkAction.BulkUpdateDetails:
          return bulkDraftUpdateDetails(booking, details);
        case BulkAction.BulkUpdateDuration:
          return bulkDraftUpdateDuration(booking, duration);
        default:
          return booking;
      }
    },
    [bulkAction, mode, date, bookingsRangeMinDate, details, duration]
  );

  const performBulkDraftOperation = useDraftOperation(
    EditMode.DraftBulkOperation,
    affectedBookingIds,
    transformer
  );

  return {
    performBulkDraftOperation
  };
};
